import * as Sentry from "@sentry/react";

/**
 * Initialise Sentry for the Boply web frontend.
 * Call once in main.tsx BEFORE ReactDOM.createRoot.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn("[Sentry] VITE_SENTRY_DSN not set — error tracking disabled");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // "development" | "production"
    release: `boply-web@${import.meta.env.VITE_APP_VERSION ?? "0.0.0"}`,

    // Performance — 10 % of transactions
    tracesSampleRate: 0.1,

    // Session Replay — off by default, 10 % of errors get replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // PII masking — block all text and user input in replays
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // PII scrubbing — strip email, name, phone, passwords from events
    beforeSend(event) {
      // Strip user PII — only keep id (UUID)
      if (event.user) {
        const { id } = event.user;
        event.user = id ? { id } : {};
      }

      // Strip request bodies that may contain passwords/tokens
      if (event.request?.data) {
        event.request.data = "[Filtered]";
      }

      // Walk breadcrumbs and scrub PII from messages
      if (event.breadcrumbs) {
        for (const bc of event.breadcrumbs) {
          if (bc.data) {
            for (const key of Object.keys(bc.data)) {
              const k = key.toLowerCase();
              if (k.includes("email") || k.includes("password") || k.includes("phone") || k.includes("token") || k.includes("name")) {
                bc.data[key] = "[Filtered]";
              }
            }
          }
        }
      }

      return event;
    },
  });
}

// Re-export for convenience in components
export { Sentry };
